import React, { useEffect, useState, useContext } from 'react';
import { InterfaceContext } from './store/InterfaceProvider';
import { useMultiFetch } from './hooks/UseMultiFetch';
import { endpoints as endpointurls, getScaleObject } from './store/StaticVariables';
import Chart from './Chart';

const Trends = () => {
    const { selectedFeature, activeViewType, activeScale, activeTimePeriod, activeDeathType, activeMeasurement, setAppError, token } = useContext(InterfaceContext);
    const baseURLS = {
        us: endpointurls.trends + getScaleObject('us').trendID,
        usstate: endpointurls.trends + getScaleObject('usstate').trendID,
        county: endpointurls.trends + getScaleObject('county').trendID,
        census: endpointurls.trends + getScaleObject('census').trendID,
        zcta: endpointurls.trends + getScaleObject('zcta').trendID
    };
    const [endpoints, setEndpoints] = useState([baseURLS.us + '/query?where=1%3D1&outFields=*&returnGeometry=false&orderByFields=Period&f=pjson']);
    const { responses, loading, error } = useMultiFetch(endpoints, token, endpointurls.useToken);
    const [lineChartData, setLineChartData] = useState([]);
    const [barChartData, setBarChartData] = useState([]);
    const isValidTimeForCensus = activeTimePeriod === 'ttm' || activeTimePeriod === '2023' || activeTimePeriod === '2022' ? true : false;
    const isReset = activeScale === 'county' && selectedFeature.GEOID.length > 5 && !isValidTimeForCensus ? true : false;

    useEffect(() => {
        const newEndpoints = selectedFeature.GEOID !== '1' ? updateEndpoints(activeScale, selectedFeature) : [];
        setEndpoints(newEndpoints);
    }, [selectedFeature]);
    useEffect(() => {
        if (!error) return;
        setAppError(true, 'Trends Chart', error);
        console.log(error);
        console.log(error?.cause);
    }, [error]);

    const updateEndpoints = (scale, selection) => {
        let arr = [];
        if (scale === 'usstate') {
            arr = [`${baseURLS.us}/query?where=1%3D1&outFields=*&returnGeometry=false&orderByFields=Period&f=pjson`, `${baseURLS.usstate}/query?where=GEOID%3D%27${selection.GEOID}%27&outFields=*&returnGeometry=false&f=pjson`];
        } else if (scale === 'county') {
            arr = [`${baseURLS.us}/query?where=1%3D1&outFields=*&returnGeometry=false&orderByFields=Period&f=pjson`, `${baseURLS.usstate}/query?where=GEOID%3D%27${selection.ST_GEOID}%27&outFields=*&returnGeometry=false&f=pjson`, `${baseURLS.county}/query?where=GEOID%3D%27${selection.GEOID}%27&outFields=*&returnGeometry=false&f=pjson`];
        } else if (scale === 'census') {
            arr = [`${baseURLS.us}/query?where=1%3D1&outFields=*&returnGeometry=false&orderByFields=Period&f=pjson`, `${baseURLS.usstate}/query?where=GEOID%3D%27${selection.ST_GEOID}%27&outFields=*&returnGeometry=false&f=pjson`, `${baseURLS.census}/query?where=GEOID%3D%27${selection.GEOID}%27&outFields=*&returnGeometry=false&f=pjson`];
        } else {
            arr = [`${baseURLS.us}/query?where=1%3D1&outFields=*&returnGeometry=false&orderByFields=Period&f=pjson`, `${baseURLS.usstate}/query?where=GEOID%3D%27${selection.ST_GEOID}%27&outFields=*&returnGeometry=false&f=pjson`, `${baseURLS.zcta}/query?where=GEOID%3D%27${selection.GEOID}%27&outFields=*&returnGeometry=false&f=pjson`];
        }
        return arr;
    };

    useEffect(() => {
        if (!loading && !error && responses.length > 0) {
            const filterChartData = (array, view, type, period, selection, measure, scale) => {
                //data resides in the features property
                const data = array.map((obj) => obj?.features);
                //destructure data
                const [nationalData, ...remainingData] = data;
                const nationalDataArray = [nationalData][0];
                let selectionDataArray = data.length > 1 ? remainingData : [];

                const convertTimestampToDate = (unix) => {
                    //add a day and return date string in 'YYYY-MM-DD' format
                    const date = new Date(unix);
                    date.setDate(date.getDate() + 1);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const dateString = `${year}-${month}-${day}`;
                    return dateString;
                };
                //create data structure for the bar chart and timeseries chart
                const structureNationalData = (arr) => {
                    //filter based on user selected type
                    const filtered = view === 'deaths' ? arr.filter((obj) => obj?.attributes?.Intent.toLowerCase() === type) : arr.filter((obj) => obj?.attributes?.Intent.toLowerCase() === 'ed');
                    //initialize result object
                    const result = {
                        monthly: [], //used for timeseries chart
                        yearly: {} //used for bar chart
                    };
                    //process the filtered array
                    filtered.forEach(({ attributes }) => {
                        attributes.Period = convertTimestampToDate(attributes.Period);
                        const measureValue = measure === 'rate' ? 'Rate' : 'Count';
                        if (attributes.Type === 'month') {
                            result.monthly.push({ date: attributes?.Period, value: attributes[measureValue] });
                        } else if (attributes.Period.includes(period)) {
                            result.yearly = { date: attributes?.Period?.slice(0, 4), name: 'United States', value: attributes.Rate };
                        } else if (period === 'ttm') {
                            result.yearly = { date: attributes?.Period?.slice(0, 4), name: 'United States', value: attributes.Rate };
                        }
                    });
                    return result;
                };

                const usData = structureNationalData(nationalDataArray);

                const filterSelectionTimeseries = (arr, view, type, selection) => {
                    let timeseriesData = [];
                    if (!arr.length) return timeseriesData;
                    if (view === 'ed_visits') {
                        timeseriesData = arr.flatMap((subArray) => subArray.filter((obj) => obj.attributes.GEOID === selection.GEOID && obj.attributes.Intent?.toLowerCase() === 'ed' && obj.attributes.Period !== 'TTM'));
                    } else {
                        timeseriesData = arr.flatMap((subArray) => subArray.filter((obj) => obj.attributes.GEOID === selection.GEOID && obj.attributes.Intent.toLowerCase() === type && obj.attributes.Period !== 'TTM'));
                    }
                    let result = timeseriesData.map((obj) => {
                        return {
                            name: obj.attributes?.NAME,
                            date: `${obj.attributes?.Period}-01-01`,
                            value: obj.attributes?.Rate,
                            modeled: obj.attributes?.Rate_M === 1 ? true : false
                        };
                    });
                    let sortedResult = [...result].sort((a, b) => new Date(a.date) - new Date(b.date));
                    return sortedResult;
                };

                const formatName = (string) => {
                    if (!string.includes(',')) return string;
                    const appendedGroupScale = {
                        zcta: 'ZIP Codes',
                        census: 'Census Tracts'
                    };
                    const strArray = string.split(', ');
                    return `${strArray.length} Grouped ${appendedGroupScale[scale]}`;
                };

                const filterSelectionCompare = (arr, view, type, period, selection, scale) => {
                    let compareData = [];
                    if (!arr.length) return compareData;
                    if (view === 'ed_visits') {
                        compareData = arr.flatMap((subArray) => subArray.filter((obj) => obj?.attributes?.GEOID?.slice(0, 2) === selection?.GEOID?.slice(0, 2) && obj?.attributes?.Intent?.toLowerCase() === 'ed' && obj?.attributes?.Period.toLowerCase() === period));
                    } else if (scale !== 'census') {
                        compareData = arr.flatMap((subArray) => subArray.filter((obj) => obj?.attributes?.GEOID?.slice(0, 2) === selection?.GEOID?.slice(0, 2) && obj?.attributes?.Intent?.toLowerCase() === type && obj?.attributes?.Period?.toLowerCase() === period));
                    } else {
                        compareData = arr.flatMap((subArray) => subArray.filter((obj) => (obj?.attributes?.GEOID?.slice(0, 2) === selection?.ST_GEOID?.slice(0, 2) || obj?.attributes?.ST_GEOID?.slice(0, 2) === selection?.ST_GEOID?.slice(0, 2)) && obj?.attributes?.Intent?.toLowerCase() === type && obj?.attributes?.Period?.toLowerCase() === period));
                    }
                    let result = compareData.map((obj) => {
                        return {
                            name: formatName(obj.attributes?.NAME),
                            date: obj.attributes?.Period,
                            value: obj.attributes?.Rate,
                            modeled: obj.attributes?.Rate_M === 1 ? true : false
                        };
                    });
                    return result;
                };

                const timeseriesData = selection.GEOID === '1' || isReset ? usData.monthly : filterSelectionTimeseries(selectionDataArray, view, type, selection);

                const selectionBarChartData = filterSelectionCompare(selectionDataArray, view, type, period, selection, scale);
                const combinedBarChartData = [usData.yearly, ...selectionBarChartData];
                if (selection.GEOID !== '1') setBarChartData(combinedBarChartData);
                return timeseriesData;
            };
            const result = filterChartData(responses, activeViewType, activeDeathType, activeTimePeriod, selectedFeature, activeMeasurement, activeScale);
            setLineChartData(result);
        }
    }, [responses, activeViewType, activeDeathType, activeTimePeriod, activeMeasurement, selectedFeature]);

    return (
        <div id="trends-wrapper" style={{ width: '100%', height: 'auto', minHeight: '60vh', display: 'flex', flexWrap: 'wrap' }}>
            {isReset || selectedFeature.GEOID === '1' ? (
                <div style={{ width: '100%', height: '100%' }}>
                    <Chart data={lineChartData} chartType="line" scale={activeScale} selection={selectedFeature} view={activeViewType} measure={activeMeasurement} deathType={activeDeathType} period={activeTimePeriod} />
                </div>
            ) : (
                <>
                    <div className="chart-container" style={{ width: '100%', height: '100%', flexBasis: '100%' }}>
                        <Chart data={barChartData} chartType="bar" isHorizontal={true} scale={activeScale} selection={selectedFeature} view={activeViewType} measure={activeMeasurement} deathType={activeDeathType} period={activeTimePeriod} />
                    </div>
                    <div className="chart-container" style={{ width: '100%', height: '100%', flexBasis: '100%' }}>
                        <Chart data={lineChartData} chartType="bar" isHorizontal={false} scale={activeScale} selection={selectedFeature} view={activeViewType} measure={activeMeasurement} deathType={activeDeathType} period={activeTimePeriod} />
                    </div>
                </>
            )}
        </div>
    );
};

export default Trends;
